/**
 * Order giveaway: a club names a prize and fans may enter their email with an
 * order. The email lands in orders.giveaway_email — a column deliberately
 * kept OUT of the anon-readable get_receipt_data / get_order_status RPCs
 * (unlike customer_email, which the public receipt route can return); it is
 * visible only to the club in All Orders and the CSV export.
 *
 * Prize resolution mirrors resolveOrderReceivedNotice: the club_settings
 * columns are the on/off switch AND the source text, per-locale wording lives
 * in club_translations under GIVEAWAY_PRIZE_KEY, and `t(key) === key` is the
 * sentinel for "untranslated" (the key has no DEFAULT_TRANSLATIONS entry).
 *
 * The same email rules run in the create-payment-intent edge function (Deno
 * twin: supabase/functions/_shared/giveaway.ts — keep equivalent) so a
 * tampered request can't store junk or enter a disabled giveaway.
 */

export const GIVEAWAY_PRIZE_KEY = 'ordering.giveawayPrize';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmailAddress(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

/**
 * The prize the fan can win, in their language, or '' for "no giveaway".
 * The columns gate everything: a stale translation row can never resurrect a
 * disabled giveaway, and a blank translation means "untranslated", not "off".
 */
export function resolveGiveawayPrize(
  settings:
    | { giveaway_enabled?: boolean | null; giveaway_prize?: string | null }
    | null
    | undefined,
  t: (key: string) => string,
): string {
  if (!settings?.giveaway_enabled) return '';
  const base = (settings.giveaway_prize ?? '').trim();
  if (!base) return '';

  const translated = t(GIVEAWAY_PRIZE_KEY);
  if (translated === GIVEAWAY_PRIZE_KEY) return base;
  return translated.trim() || base;
}

/**
 * What an order should store: a trimmed, lowercased, valid address — or null
 * when the giveaway is disabled, the field was left empty, or the input
 * doesn't look like an email.
 */
export function normalizeGiveawayEmail(
  email: string | null | undefined,
  giveawayEnabled: boolean,
): string | null {
  if (!giveawayEnabled) return null;
  const trimmed = (email ?? '').trim().toLowerCase();
  if (!trimmed || !EMAIL_RE.test(trimmed)) return null;
  return trimmed;
}
